import React, { useState } from 'react';
import { Form } from 'semantic-ui-react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { GenericScaledField } from 'src/components/controls/rhf/GenericScaledField';
import { getWithAuth } from 'src/api';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import './ScanLossConfigGroup.css';

const AZIMUTH_LIMIT = 60; // +/-
const ELEVATION_LIMIT = 15; // +/-

const isValid = (num, limit) => {
  return !isNaN(parseInt(num)) && num >= -limit && num <= limit;
};

const isValidFrequencyMhz = (frequencyMhz: any) => {
  const freq = Number(frequencyMhz);
  return Number.isFinite(freq) && freq > 0;
};

async function fetchScanLoss(
  projectId,
  frequencyMhz,
  azimuthOffset,
  elevationOffset
) {
  if (
    isValidFrequencyMhz(frequencyMhz) &&
    isValid(azimuthOffset, AZIMUTH_LIMIT) &&
    isValid(elevationOffset, ELEVATION_LIMIT)
  ) {
    try {
      return await getWithAuth(
        `project/${projectId}/ptp/scan_loss/${frequencyMhz}/${azimuthOffset}/${elevationOffset}`
      );
    } catch (e) {
      return 0;
    }
  } else {
    return 0;
  }
}

type Props = {
  pathPrefix?: string;
  pathIndex?: number;
  frequencyMhz: any;
  calculateDefaultValue: any;
  setModified: React.Dispatch<React.SetStateAction<boolean>>;
  formResetValue: number;
};

function ScanLossConfigGroup(props: Props) {
  const {
    pathPrefix = '',
    pathIndex = 0,
    frequencyMhz,
    calculateDefaultValue,
    setModified,
    formResetValue,
  } = props;

  const { control, getValues, setValue } = useFormContext();
  const [refreshText, setRefreshText] = useState(0);

  const calculateAttrPath = `${pathPrefix}radios.${pathIndex}.antennas.0.config.user_calculate_scan_loss`;
  const scanLossPath = `${pathPrefix}radios.${pathIndex}.antennas.0.config.scan_loss`;
  const azimuthOffsetPath = `${pathPrefix}radios.${pathIndex}.antennas.0.config.azimuth_offset`;
  const elevationOffsetPath = `${pathPrefix}radios.${pathIndex}.antennas.0.config.elevation_offset`;

  const calculateScanLoss = useWatch({
    name: calculateAttrPath,
    control,
    defaultValue: calculateDefaultValue,
  });

  const projectId = useSelector((state: any) => state.mainFrame.projectId);
  const permissionWrite = useSelector(
    (state: any) => state.mainFrame.permissionWrite
  );
  const azimuthOffset = getValues(azimuthOffsetPath);
  const elevationOffset = getValues(elevationOffsetPath);
  const queryKey = [
    projectId,
    frequencyMhz,
    azimuthOffset,
    elevationOffset,
    calculateScanLoss,
  ];

  const { refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const fetchedLoss = await fetchScanLoss(
        projectId,
        frequencyMhz,
        azimuthOffset,
        elevationOffset
      );

      setValue(scanLossPath, fetchedLoss, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });

      return fetchedLoss;
    },
    retry: false,
    refetchOnWindowFocus: true,
    enabled: false,
  });

  return (
    <>
      <div className="scanLossConfigGroup">
        <div>
          <GenericScaledField
            label="Scan Loss"
            getter={scanLossPath}
            defaultValue={0}
            units="dB"
            min={0}
            max={10}
            precision={1}
            disabled={calculateScanLoss}
            alwaysEnabled={false}
            className="scanLossMax8em"
            setModified={setModified}
            watch={refreshText}
          />
        </div>

        <div>
          <Form.Field>
            <Controller
              control={control}
              name={calculateAttrPath}
              defaultValue={calculateScanLoss}
              render={({ field: { onChange, value } }) => {
                return (
                  <Form.Checkbox
                    label="Calculate?"
                    disabled={!permissionWrite}
                    onChange={(e, { checked }) => {
                      refetch();
                      setModified(true);
                      onChange(checked);
                      if (checked) {
                        setRefreshText((c) => c + 1);
                      }
                    }}
                    checked={value}
                  />
                );
              }}
            />
          </Form.Field>
        </div>

        {calculateScanLoss && (
          <div className="scanLossOffsetPair">
            <GenericScaledField
              label="Azimuth Offset"
              getter={azimuthOffsetPath}
              defaultValue={0}
              units="°"
              min={-AZIMUTH_LIMIT}
              max={AZIMUTH_LIMIT}
              precision={0}
              alwaysEnabled={false}
              watch={formResetValue}
              className="scanLossMax6em"
              setModified={setModified}
              onAcceptChange={() => {
                refetch();
                setModified(true);
              }}
            />
            <GenericScaledField
              label="Elevation Offset"
              getter={elevationOffsetPath}
              defaultValue={0}
              units="°"
              min={-ELEVATION_LIMIT}
              max={ELEVATION_LIMIT}
              precision={0}
              alwaysEnabled={false}
              watch={formResetValue}
              className="scanLossMax6em"
              setModified={setModified}
              onAcceptChange={() => {
                refetch();
                setModified(true);
              }}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default ScanLossConfigGroup;
